import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Empty, Flex, Pagination, Table } from 'antd';
import { EmptyIcon } from '@/assets/icons';
import { DataTableSkeleton } from '@/components/skeletons';
import { useDebounce } from '@/hooks';
import { cn } from '@/libs/utils';
import { PAGE_SIZE_OPTIONS } from '@/shared/constants';
import { ContentBody, ContentHeader, ContentWrapper } from '../content';
import { PageSizeDropdown } from './PageSizeDropdown';
import { PaginationControls } from './PaginationControls';

export default function DataTable({
  data,
  total,
  title,
  status,
  columns,
  className,
  offset,
  limit,
  tableClassName,
  onChangePagination,
  // Called alongside the pager's own handler with antd's full change payload,
  // so a list can drive column sorting and column filters from the same table
  // event. Optional: lists that do neither pass nothing and behave as before.
  onTableChange,
  headerChildren,
  // The page-size picker beside the pager instead of in the header. For the
  // invoice books, whose header has no room to spare; every other list keeps
  // it where it was.
  pageSizeInFooter = false,
  metadata,
  rowKey = '_id',
  // Page and page size live in the URL's `offset` / `limit` by default. A list
  // embedded in another page (a profile panel) keeps its own state instead and
  // passes `syncUrl={false}`: the table then reads `offset` / `limit` from its
  // props only and leaves the URL to `onChangePagination`.
  syncUrl = true,
  ...rest
}) {
  const { t } = useTranslation('tables');

  const [searchParams, setSearchParams] = useSearchParams();
  // Read straight off the URL. They used to be copied into state by an
  // effect, which cost every page turn one more render of the whole table.
  const page =
    (syncUrl && parseInt(searchParams.get('offset'))) || parseInt(offset) || 1;
  const pageSize =
    (syncUrl && parseInt(searchParams.get('limit'))) || parseInt(limit) || 10;

  const handleTableChange = useDebounce(
    useCallback(
      ({ current, pageSize }, tableFilters, sorter, extra) => {
        if (typeof onTableChange === 'function') {
          onTableChange({ filters: tableFilters, sorter, extra });
        }

        if (typeof onChangePagination !== 'function') {
          throw new TypeError('Miss or invalid onChangePagination function!');
        }

        // 🚨 Sorting and column filters arrive through this same event, and the
        // handler above has just written them into the URL. Writing the params
        // captured *before* that call would put the old URL back and undo it —
        // which is exactly what a click on a column sorter did: the handler
        // ran, the parameter was set, and it disappeared in the same tick.
        //
        // The pager is a sibling of the table (`pagination={false}`), so antd
        // sends no page here at all; there is nothing to write unless a page
        // actually changed.
        const hasPageChange = parseInt(current) || parseInt(pageSize);
        if (!hasPageChange) return;

        if (!syncUrl) {
          if (parseInt(current))
            onChangePagination('offset', parseInt(current));
          if (parseInt(pageSize))
            onChangePagination('limit', parseInt(pageSize));
          return;
        }

        const newSearchParams = new URLSearchParams(searchParams);

        if (parseInt(current)) {
          onChangePagination('offset', parseInt(current));
          newSearchParams.set('offset', parseInt(current));
        }

        if (parseInt(pageSize)) {
          onChangePagination('limit', parseInt(pageSize));
          newSearchParams.set('limit', parseInt(pageSize));
        }

        setSearchParams(newSearchParams);
      },
      [
        onChangePagination,
        onTableChange,
        searchParams,
        setSearchParams,
        syncUrl,
      ]
    ),
    50
  );

  const showPagination = total > pageSize;

  const pageSizeDropdown = (
    <PageSizeDropdown
      data={data}
      status={status}
      page={parseInt(page)}
      pageSize={parseInt(pageSize)}
      handleTableChange={handleTableChange}
    />
  );

  const pageSizeOptions = PAGE_SIZE_OPTIONS.map(({ value }) =>
    value.toString()
  );

  return (
    <ContentWrapper className={cn(metadata?.contentWrapperClass)}>
      {title && (
        <ContentHeader title={title}>
          {headerChildren}

          {!pageSizeInFooter && pageSizeDropdown}
        </ContentHeader>
      )}

      {/* 🚨 The caller's height goes on this box, not on the scroller inside
          it. Every list asks for `h-[calc(100svh - Npx)]`, and that figure was
          worked out when the pager still sat *inside* the table. Moved out, the
          pager became a sibling and its 48px hung off the bottom of the screen
          — measured at 16px past the fold with room for nothing else. Held
          here, the scroller takes what is left after the pager has had its
          share, and every list gets that for free rather than each one having
          its own number corrected by hand. */}
      <ContentBody className={cn('flex flex-col', className)}>
        <Flex vertical gap="middle" className="min-h-0 flex-1 overflow-y-auto">
          {status === 'pending' ? (
            <DataTableSkeleton columnLength={metadata?.columnLength} />
          ) : (
            <Table
              rowKey={rowKey}
              columns={columns}
              dataSource={data}
              onChange={handleTableChange}
              locale={{
                emptyText: (
                  <Empty
                    className="grid gap-2 py-8"
                    description={t('noData')}
                    image={<EmptyIcon />}
                  />
                ),
              }}
              // 🚨 Never antd's own pager. It renders inside the table, so it
              // takes the width of the *content* rather than of the screen —
              // measured on the book: 2.788px wide against 1.540px visible,
              // with its right edge 1.250px past the fold. Centred in that
              // width, the page numbers sit off to the right and a person on a
              // narrower laptop sees no pager at all until they scroll
              // sideways. It is rendered below instead, outside the scroller.
              pagination={false}
              loading={{
                spinning: false,
              }}
              {...rest}
              className={cn('ant-table-content', tableClassName)}
            />
          )}
        </Flex>

        {/* Outside the scrolling box on purpose — see the note on
            `pagination={false}` above. Here it measures itself against the
            screen, so it stays put however wide the table gets. */}
        {pageSizeInFooter && status !== 'pending' && (
          // Three columns so the pager stays centred on the screen with the
          // picker at the right edge; an empty left column balances it.
          <div className="mt-2 grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-4 border-t border-gray-100 px-4 pb-5 pt-4 dark:border-gray-700">
            <span />
            {showPagination ? (
              <Pagination
                align="center"
                total={total}
                pageSize={pageSize}
                current={page}
                pageSizeOptions={pageSizeOptions}
                showSizeChanger={false}
                showQuickJumper={false}
                onChange={(current, size) =>
                  handleTableChange({ current, pageSize: size })
                }
                itemRender={(page, type, originalElement) => (
                  <PaginationControls
                    type={type}
                    originalElement={originalElement}
                  />
                )}
              />
            ) : (
              <span />
            )}
            <div className="flex justify-end">{pageSizeDropdown}</div>
          </div>
        )}

        {!pageSizeInFooter && showPagination && status !== 'pending' && (
          <Pagination
            // Air on both sides and a hairline above. Sitting flush against the
            // panel's bottom edge, the pager read as part of the border, and
            // the last row of the table ran straight into it with no seam to
            // say where the data stopped.
            className="mt-2 shrink-0 justify-center border-t border-gray-100 pb-5 pt-4 dark:border-gray-700"
            align="center"
            total={total}
            pageSize={pageSize}
            current={page}
            pageSizeOptions={pageSizeOptions}
            showSizeChanger={false}
            showQuickJumper={false}
            onChange={(current, size) =>
              handleTableChange({ current, pageSize: size })
            }
            itemRender={(page, type, originalElement) => (
              <PaginationControls
                type={type}
                originalElement={originalElement}
              />
            )}
          />
        )}
      </ContentBody>
    </ContentWrapper>
  );
}
